import React, { useEffect, useState } from 'react'
import { IcArrowRight, IcCross } from '@healingpaper/cell-icons'

import Text from '@/components/atomic/atoms/Text'
import SearchInput from '@/components/atomic/molecules/SearchInput'
import Badge from '@/components/cell/molecules/Badge'
import Button from '@/components/cell/molecules/Button'
import { getSourceDomainFromUrlPath } from '@/core/lib'
import { AirbridgeEvents, sendAirbridgeEvent } from '@/utils/airbridge'
import { TreatmentCategory } from '@/features/treatment-category-filter/types/treatment-filter.type'
import FlexCol from '@/components/shared/FlexCol'

const MIN_SHOW_TREATMENT_LENGTH = 8

interface Props {
  isShowSearchPopup: boolean
  treatmentCategory: {
    title: string
    moreText: string
    list: TreatmentCategory[]
  }
  keyword: {
    title: string
    list: string[]
  }
  placeholder: string
  onClickMoreTreatment: () => void
  onClickTreatment: (treatment: TreatmentCategory) => void
  onClickClose: () => void
  onSearch: (value: string, action: 'click_button' | 'press_enter', type: 'input' | 'popular') => void
}

const SearchPopupContent = ({
  treatmentCategory,
  keyword,
  placeholder,
  isShowSearchPopup,
  onClickTreatment,
  onClickMoreTreatment,
  onClickClose,
  onSearch,
}: Props): JSX.Element => {
  const [treatmentLength, setTreatmentLength] = useState(MIN_SHOW_TREATMENT_LENGTH)

  const handlePressEnterInSearchInput = (value: string, type: 'button' | 'keyboard') => {
    onSearch(value, type === 'button' ? 'click_button' : 'press_enter', 'input')
  }

  const handleClickPopularKeyword = (keyword: string) => {
    onSearch(keyword, 'press_enter', 'popular')
  }

  const handleClickMoreTreatment = () => {
    setTreatmentLength(treatmentCategory.list.length)
    onClickMoreTreatment()
  }

  useEffect(() => {
    if (isShowSearchPopup) {
      ;(document.querySelector('#searchInput:not([disabled])') as HTMLInputElement)?.focus()
    }
  }, [isShowSearchPopup])

  useEffect(() => {
    if (isShowSearchPopup) {
      sendAirbridgeEvent(AirbridgeEvents.SEARCH_VIEW, {
        semanticAttributes: {
          referrer: getSourceDomainFromUrlPath(window.location.pathname),
        },
      })
    }
  }, [isShowSearchPopup])

  return (
    <>
      <div className="h-12 flex flex-row-reverse box-border p-3">
        <button
          className="w-6 h-6 p-0 leading-none border-none bg-transparent box-border cursor-pointer [&>*]:fill-[#4c4c4c] [&>*]:text-[#4c4c4c]"
          onClick={onClickClose}
        >
          <IcCross />
        </button>
      </div>
      <div className="flex-1 overflow-auto">
        <div>
          <SearchInput placeholder={placeholder} onPressEnter={handlePressEnterInSearchInput} />
        </div>
        <FlexCol className="px-[26px] pt-[26px]">
          <Text type="title3" element="h3">
            {treatmentCategory.title}
          </Text>
          <ol className="list-none p-0 m-0 mt-4 grid grid-cols-4 gap-2 box-border place-items-center">
            {treatmentCategory.list.slice(0, treatmentLength).map((treatment, index) => (
              <li key={treatment.id} className="w-full flex flex-col justify-center items-center">
                <div
                  role="button"
                  tabIndex={index}
                  onClick={() => onClickTreatment(treatment)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') onClickTreatment(treatment)
                  }}
                  className="relative mx-auto z-[1] flex flex-col justify-center items-center rounded-lg w-full max-w-[80px] min-h-[80px] bg-white cursor-pointer hover:shadow-[0px_0px_8px_rgba(0,0,0,0.16)]"
                >
                  <img src={treatment.iconImageUrl} alt={treatment.name} width={32} height={32} />
                  <Text element="span" type="caption2" className="text-center">
                    {treatment.name}
                  </Text>
                </div>
              </li>
            ))}
          </ol>
          {treatmentLength === MIN_SHOW_TREATMENT_LENGTH && (
            <Button
              size="medium"
              color="tertiary"
              onClick={handleClickMoreTreatment}
              className="w-full flex items-center justify-center mt-6"
            >
              <Text type="title4">{treatmentCategory.moreText}</Text>
              <IcArrowRight width={16} height={16} />
            </Button>
          )}
        </FlexCol>
        {keyword.list.length > 0 && (
          <FlexCol className="px-[26px] pt-[26px]">
            <Text type="title3" element="h3">
              {keyword.title}
            </Text>
            <div className="flex flex-wrap gap-2 mt-4">
              {keyword.list.map((keyword, index) => (
                <div
                  key={index}
                  onClick={() => handleClickPopularKeyword(keyword)}
                  className="mr-2 inline-block mt-4 cursor-pointer"
                >
                  <Badge>{keyword}</Badge>
                </div>
              ))}
            </div>
          </FlexCol>
        )}
      </div>
    </>
  )
}

const DelayedImage = ({ src, alt, width }: { src: string; alt: string; width: number }) => {
  const [imageSrc, setImageSrc] = useState('')

  useEffect(() => {
    setImageSrc(src)
  }, [src])

  return (
    <img
      src={imageSrc}
      alt={alt}
      width={width}
      height={width}
      className={`mb-2.5 ${imageSrc ? 'opacity-100' : 'opacity-0'}`}
    />
  )
}

export default SearchPopupContent
